import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton, Switch, Checkbox, TextField } from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { axiosPrivate } from '../../../axios/axios';
import { masterAPI } from '../dataConfig';
import { color } from '../../../StyleData/Config';
import TableContainer from '../../../Component/TableContainer';

const EditableCell = ({
                          cell,
                          field,
                          valueType,
                          inputType = 'text',
                          onCellChange,
                          changes
                      }) => {
    const canEdit = cell.row.original.attributeValueType === valueType;
    const rowId = cell.row.original.id;

    // Create a unique key for this specific cell
    const cellKey = `${rowId}-${field}`;

    // Get the original value from cell
    const getOriginalValue = () => {
        if (field === 'attributeDateValue' && cell.value) {
            return new Date(cell.value).toISOString().split('T')[0];
        } else if (field === 'attributeNumericValue') {
            return cell.value !== null && cell.value !== undefined ? cell.value.toString() : '';
        } else {
            return cell.value || '';
        }
    };

    // Get the changed value for THIS specific cell only
    const getChangedValue = () => {
        if (changes && changes[rowId] && changes[rowId][field] !== undefined) {
            return changes[rowId][field];
        }
        return null;
    };

    const changedValue = getChangedValue();
    const originalValue = getOriginalValue();

    // Initialize with changed value if exists, otherwise original
    const [localValue, setLocalValue] = React.useState(
        changedValue !== null ? changedValue : originalValue
    );
    const [isFocused, setIsFocused] = React.useState(false);

    // Store the previous changed value to detect real changes for THIS cell
    const prevChangedValueRef = React.useRef(changedValue);

    // Only sync when THIS cell's changed value actually changes (e.g., after save/reset)
    React.useEffect(() => {
        // Only update if not focused AND the changed value for THIS cell specifically changed
        if (!isFocused) {
            const currentChangedValue = getChangedValue();
            const prevChangedValue = prevChangedValueRef.current;

            // If changes were cleared (after save), reset to original
            if (prevChangedValue !== null && currentChangedValue === null) {
                setLocalValue(getOriginalValue());
            }
            // If this cell's change was updated externally
            else if (currentChangedValue !== null && currentChangedValue !== prevChangedValue) {
                setLocalValue(currentChangedValue);
            }

            prevChangedValueRef.current = currentChangedValue;
        }
    }, [changes, rowId, field, isFocused]);

    // Reset when cell.value changes (after save)
    React.useEffect(() => {
        if (!isFocused && getChangedValue() === null) {
            setLocalValue(getOriginalValue());
        }
    }, [cell.value]);

    const handleChange = (e) => {
        setLocalValue(e.target.value);
    };

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        onCellChange(rowId, field, localValue);
        setIsFocused(false);
    };

    if (!canEdit) {
        const formatDisplayValue = () => {
            if (field === 'attributeDateValue' && cell.value) {
                return new Date(cell.value).toLocaleDateString();
            }
            if (field === 'attributeNumericValue') {
                return cell.value !== null && cell.value !== undefined && cell.value !== 0 ? cell.value : '';
            }
            return cell.value || '';
        };
        return (
            <Typography sx={{ padding: '4px 8px', minHeight: '20px', display: 'flex', alignItems: 'center' }}>
                {formatDisplayValue() || '-'}
            </Typography>
        );
    }

    const isChanged = localValue !== originalValue;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
            <TextField
                key={cellKey}
                value={localValue}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                size="small"
                variant="outlined"
                type={inputType}
                sx={{
                    minWidth: 120,
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: isChanged ? '#fff3cd' : 'transparent'
                    }
                }}
                placeholder={`Enter ${valueType.toLowerCase()} value`}
            />
        </Box>
    );
};

const ModelSpecAttribute = () => {
  const actionsAllow = useSelector(state => state.dataRed.actionsAllow);
  const navigate = useNavigate();
  const location = useLocation();
  
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterValue, setFilterValue] = useState("enableData");
  const [backUpData, setBackUpData] = useState([]);
  const [modelData, setModelData] = useState(null);
  const [changes, setChanges] = useState({}); // { [rowId]: { field: value, ... }, ... }

  useEffect(() => {
    if (location.state?.modelData) {
      setModelData(location.state.modelData);
    } else {
      navigate('/masters/model');
    }
  }, [location, navigate]);

  const fetchAttributes = useCallback(async () => {
    if (!modelData?.id) return;
    
    setLoading(true);
    try {
      const link = masterAPI["model_spec_attributes"].getAllByModelId;
      const res = await axiosPrivate.get(`${link}/${modelData.id}`);
      const data = res.data.result || res.data || [];
      
      data.sort((a, b) => {
        return a.attributeName?.localeCompare(b.attributeName, undefined, {
          numeric: true,
          sensitivity: 'base'
        }) || 0;
      });

      if (filterValue === "enableData") {
        const filterDataResult = data.filter(fil => fil.isActive);
        setAttributes(filterDataResult);
        setBackUpData(data);
      } else {
        setAttributes(data);
        setBackUpData(data);
      }
    } catch (err) {
      console.error('Error fetching model spec attributes:', err);
      setAttributes([]);
      setBackUpData([]);
    }
    setLoading(false);
  }, [modelData, filterValue]);

  useEffect(() => {
    if (modelData) {
      fetchAttributes();
    }
  }, [modelData, fetchAttributes]);

  const onCellChange = (rowId, field, value) => {
    setChanges(prev => {
      const prevRow = prev[rowId] || {};
      return { ...prev, [rowId]: { ...prevRow, [field]: value } };
    });
  };

  const handleSaveAll = async () => {
    const changedRows = Object.entries(changes);
    if (changedRows.length === 0) return;
    try {
      const payload = changedRows.map(([rowId, fields]) => {
        const currentItem = attributes.find(item => item.id === Number(rowId) || item.id === rowId);
        let attributeDateValue = fields.attributeDateValue !== undefined ? fields.attributeDateValue : currentItem.attributeDateValue;
        let attributeNumericValue = fields.attributeNumericValue !== undefined ? parseFloat(fields.attributeNumericValue) : currentItem.attributeNumericValue;
        let attributeTextValue = fields.attributeTextValue !== undefined ? fields.attributeTextValue : currentItem.attributeTextValue;
        if (attributeDateValue && !isNaN(Date.parse(attributeDateValue))) {
          attributeDateValue = new Date(attributeDateValue).toISOString();
        }
        return {
          modelMasterId: currentItem.modelMasterId,
          techSpecAttributeId: currentItem.techSpecAttributeId,
          attributeDateValue,
          attributeNumericValue,
          attributeTextValue,
          ...(currentItem.id ? { id: currentItem.id } : {})
        };
      });
      const bulkInsertLink = masterAPI["model_spec_attributes"].bulkinsert;
      await axiosPrivate.post(bulkInsertLink, payload);
      // Update local state
      setAttributes(prev => prev.map(item => {
        const changed = changes[item.id];
        if (!changed) return item;
        return {
          ...item,
          ...changed,
          attributeNumericValue: changed.attributeNumericValue !== undefined ? parseFloat(changed.attributeNumericValue) : item.attributeNumericValue,
          attributeDateValue: changed.attributeDateValue !== undefined ? changed.attributeDateValue : item.attributeDateValue,
          attributeTextValue: changed.attributeTextValue !== undefined ? changed.attributeTextValue : item.attributeTextValue,
        };
      }));
      setBackUpData(prev => prev.map(item => {
        const changed = changes[item.id];
        if (!changed) return item;
        return {
          ...item,
          ...changed,
          attributeNumericValue: changed.attributeNumericValue !== undefined ? parseFloat(changed.attributeNumericValue) : item.attributeNumericValue,
          attributeDateValue: changed.attributeDateValue !== undefined ? changed.attributeDateValue : item.attributeDateValue,
          attributeTextValue: changed.attributeTextValue !== undefined ? changed.attributeTextValue : item.attributeTextValue,
        };
      }));
      setChanges({});
    } catch (error) {
      console.error('Error saving all changes:', error);
    }
  };

  const handleDelete = async (e, id, code) => {
    const enableLink = masterAPI["model_spec_attributes"].enable;
    const disableLink = masterAPI["model_spec_attributes"].disable;
    
    try {
      if (e.target.checked) {
        await axiosPrivate.put(`${enableLink}/${id}/${code}`);
      } else {
        await axiosPrivate.put(`${disableLink}/${id}/${code}`);
      }
      fetchAttributes();
    } catch (error) {
      console.error('Error updating attribute status:', error);
    }
  };

  const handleChangeFilter = (e) => {
    if (e === "showAll") {
      setAttributes(backUpData);
    } else {
      const filterDataResult = backUpData.filter(fil => fil.isActive);
      setAttributes(filterDataResult);
    }
    setFilterValue(e);
  };

  const columns = [
    {
      accessor: "id",
      disableFilters: true,
      Cell: () => {
        return (
          <Box sx={{ width: color.checkBoxWidth }}>
            <Checkbox />
          </Box>
        );
      },
    },
    {
      Header: 'Model Name',
      accessor: 'modelName'
    },
    {
      Header: 'Attribute Name',
      accessor: 'attributeName',
    },
    {
      Header: 'Value Type',
      accessor: 'attributeValueType'
    },
    {
      Header: 'Text Value',
      accessor: 'attributeTextValue',
      Cell: ({ cell }) => (
        <EditableCell
          cell={cell}
          field="attributeTextValue"
          valueType="Text"
          inputType="text"
          onCellChange={onCellChange}
          changes={changes}
        />
      )
    },
    {
      Header: 'Numeric Value',
      accessor: 'attributeNumericValue',
      Cell: ({ cell }) => (
        <EditableCell
          cell={cell}
          field="attributeNumericValue"
          valueType="Numeric"
          inputType="number"
          onCellChange={onCellChange}
          changes={changes}
        />
      )
    },
    {
      Header: 'Date Value',
      accessor: 'attributeDateValue',
      Cell: ({ cell }) => (
        <EditableCell
          cell={cell}
          field="attributeDateValue"
          valueType="Datetime"
          inputType="date"
          onCellChange={onCellChange}
          changes={changes}
        />
      )
    },
    {
      Header: 'Actions',
      Cell: ({ cell }) => {
        return (
          <Box sx={{ whiteSpace: "nowrap" }}>
            <Switch
              disabled={!actionsAllow.delete}
              checked={cell.row.original.isActive}
              onChange={(e) => { handleDelete(e, cell.row.original.id, cell.row.original.code || cell.row.original.attributeCode || cell.row.original.attributeName) }}
            />
          </Box>
        );
      },
    }
  ];

  const handleBackToModel = () => {
    navigate('/masters/model');
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={handleBackToModel}>
          <ArrowBack />
        </IconButton>
        <Typography
          sx={{
            color: color.masterPageTitleColor,
            fontSize: color.masterPageTitleFontSize,
            fontWeight: color.masterPageTitleFontWeight,
          }}
        >
          {masterAPI["model_spec_attributes"].title}
        </Typography>
        {modelData && (
          <Typography
            sx={{
              color: color.masterPageTitleColor,
              fontSize: '16px',
              fontWeight: 'normal',
            }}
          >
            - {modelData.code} ({modelData.modelName})
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Box>
          <IconButton
            color="primary"
            onClick={handleSaveAll}
            disabled={Object.keys(changes).length === 0}
            title="Save All Changes"
          >
           <Save />
          </IconButton>
        </Box>
      </Box>

      <TableContainer
        columns={columns}
        data={attributes}
        loading={loading}
        buttonDisable={true}
        filterValue={filterValue}
        handleChange={handleChangeFilter}
        filterShow={true}
      />
    </>
  );
};

export default ModelSpecAttribute;
